import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Plus, X } from 'lucide-react';
import {
  GTD_STATUSES,
  GTD_STATUS_LABELS,
  KIND_LABELS,
  allowedContainerKinds,
  defaultFilingRequest,
  itemTextFromCapture,
  describeWallTime,
  isGtdStatus,
  ownContext,
  validateFilingRequest,
} from '@cerebro-diem/core';
import type { Classification, ContainerRow, FilingRequest, FilingRequestDraft, ItemKind } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { useProfile } from '../../services/profile';
import { fileCaptureInto } from '../../services/captures';
import type { CaptureWithClassifications, Filed } from '../../services/captures';
import { refreshFilingViews } from '../../services/queries';
import ContainerPicker from '../../components/ContainerPicker';
import DictationTextarea from '../../components/DictationTextarea';
import NoDeviceNote from '../../components/NoDeviceNote';
import { chipButton, chipClass } from '../../components/ContainerChip';
import { inputClass, outlineButton, primaryButton } from '../../components/styles';

export interface PersonName {
  id: string;
  name: string;
}

const KINDS: readonly ItemKind[] = ['task', 'note'];

/**
 * File (or re-file) a capture as a task or a note, starting from the AI's
 * suggestion when there is one: where it lives (or a new container), the
 * title, a task's GTD status, context and due date, and the people involved.
 */
export default function FilingForm({
  capture,
  suggestion,
  containers,
  people,
  contexts,
  onFiled,
  onCancel,
}: {
  capture: CaptureWithClassifications;
  suggestion: Classification | null;
  containers: readonly ContainerRow[];
  people: readonly PersonName[];
  contexts: readonly string[];
  onFiled: (filed: Filed, request: FilingRequest) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  // Suggested reminders are judged, and shown, in the user's zone.
  const zone = useProfile().data?.timezone ?? null;
  const [draft, setDraft] = useState<FilingRequestDraft>(() =>
    defaultFilingRequest(capture.raw_text, suggestion, containers, { timeZone: zone, now: new Date() }),
  );
  const [detailsEdited, setDetailsEdited] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (request: FilingRequest) => fileCaptureInto(capture.id, request),
    onSuccess: (filed, request) => {
      onFiled(filed, request);
      return refreshFilingViews(queryClient);
    },
  });
  const set = (patch: Partial<FilingRequestDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const setKind = (kind: ItemKind) =>
    setDraft((current) => ({
      ...current,
      kind,
      details: detailsEdited ? current.details : itemTextFromCapture(kind, capture.raw_text, current.title),
      // A task cannot stay in a Resource.
      container: current.container && allowedContainerKinds(kind).includes(current.container.kind) ? current.container : null,
    }));
  const togglePerson = (id: string) =>
    set({ personIds: draft.personIds.includes(id) ? draft.personIds.filter((p) => p !== id) : [...draft.personIds, id] });
  const toggleNewPerson = (name: string) =>
    set({
      newPersonNames: draft.newPersonNames.includes(name)
        ? draft.newPersonNames.filter((n) => n !== name)
        : [...draft.newPersonNames, name],
    });
  // Only the user's own contexts are offered or sent: a suggested one is matched as the database
  // matches it (contextKey) and sent in the user's spelling; one no longer in the list is dropped.
  const context = draft.context ? ownContext(draft.context, contexts) : null;
  const chosenPeople = people.filter((p) => draft.personIds.includes(p.id));
  const otherPeople = people.filter((p) => !draft.personIds.includes(p.id));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validateFilingRequest({ ...draft, context });
    if (!result.ok) {
      setValidation(`${result.error}.`);
      return;
    }
    setValidation(null);
    mutation.mutate(result.request);
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl bg-surface-variant/30 p-3">
      <div className="flex gap-2" role="group" aria-label="File as">
        {KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            aria-pressed={draft.kind === kind}
            onClick={() => setKind(kind)}
            className={draft.kind === kind ? primaryButton : outlineButton}
          >
            {KIND_LABELS[kind]}
          </button>
        ))}
      </div>

      <Field label="Lives in">
        <ContainerPicker
          containers={containers}
          kinds={allowedContainerKinds(draft.kind)}
          value={draft.container}
          onChange={(container) => set({ container })}
          allowNew
          label="Lives in"
        />
      </Field>

      <Field label={draft.kind === 'task' ? 'Next action (start with a verb)' : 'Title'}>
        <input className={inputClass} value={draft.title} onChange={(e) => set({ title: e.target.value })} required />
      </Field>

      {draft.kind === 'task' && (
        <div className="flex flex-wrap gap-3">
          <Field label="GTD">
            <select
              className={inputClass}
              value={draft.gtdStatus}
              onChange={(e) => {
                if (isGtdStatus(e.target.value)) set({ gtdStatus: e.target.value });
              }}
            >
              {GTD_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {GTD_STATUS_LABELS[option]}
                </option>
              ))}
            </select>
          </Field>
          {draft.gtdStatus === 'next' && (
            <Field label="Context">
              <select className={inputClass} value={context ?? ''} onChange={(e) => set({ context: e.target.value || null })}>
                <option value="">No context</option>
                {contexts.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Due date (optional)">
            <div className="flex gap-2">
              <input
                type="date"
                className={inputClass}
                value={draft.due_date ?? ''}
                onChange={(e) => set({ due_date: e.target.value || null })}
              />
              {draft.due_date && (
                <button type="button" onClick={() => set({ due_date: null })} className={outlineButton}>
                  Clear
                </button>
              )}
            </div>
          </Field>
          {draft.reminder && (
            <Field label="Reminder">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${chipClass} bg-primary-container text-primary inline-flex items-center gap-1`}>
                  <Bell className="w-3 h-3" aria-hidden="true" />
                  {zone ? describeWallTime(draft.reminder, zone, new Date()) : draft.reminder}
                </span>
                <button type="button" onClick={() => set({ reminder: null })} className={outlineButton}>
                  Clear
                </button>
              </div>
              <NoDeviceNote />
            </Field>
          )}
          {draft.expiredReminder && (
            <p className="text-sm text-on-surface-variant">
              The suggested reminder ({zone ? describeWallTime(draft.expiredReminder, zone, new Date()) : draft.expiredReminder}) had
              already passed, so none is set.
            </p>
          )}
        </div>
      )}

      <DictationTextarea
        label={draft.kind === 'task' ? 'Notes' : 'Body'}
        rows={3}
        value={draft.details ?? ''}
        onChange={(next) => {
          setDetailsEdited(true);
          set({ details: next || null });
        }}
      />

      <Field label={draft.gtdStatus === 'waiting' && draft.kind === 'task' ? 'People (who you are waiting for)' : 'People'}>
        <div className="flex flex-wrap items-center gap-1.5">
          {chosenPeople.map((person) => (
            <button
              key={person.id}
              type="button"
              onClick={() => togglePerson(person.id)}
              className={`${chipButton} bg-primary-container text-primary`}
              aria-label={`Remove ${person.name}`}
            >
              {person.name}
              <X className="w-3 h-3" />
            </button>
          ))}
          {draft.proposedPeople.map((name) => {
            const chosen = draft.newPersonNames.includes(name);
            return (
              <button
                key={name}
                type="button"
                aria-pressed={chosen}
                onClick={() => toggleNewPerson(name)}
                className={`${chipButton} ${chosen ? 'bg-primary-container text-primary' : 'border border-outline/30 text-on-surface-variant'}`}
              >
                {chosen ? (
                  <>
                    {name} (new) <X className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    <Plus className="w-3 h-3" /> Add {name}
                  </>
                )}
              </button>
            );
          })}
          {otherPeople.length > 0 && (
            <select
              className={`${inputClass} w-auto`}
              value=""
              onChange={(e) => {
                if (e.target.value) togglePerson(e.target.value);
              }}
              aria-label="Add a person"
            >
              <option value="">Add a person…</option>
              {otherPeople.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </Field>

      {validation && <p className="text-sm text-error">{validation}</p>}
      {mutation.isError && <p className="text-sm text-error">Not filed: {errorMessage(mutation.error)}</p>}
      {mutation.isSuccess && <p className="text-sm text-success">Filed.</p>}

      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={mutation.isPending || mutation.isSuccess} className={primaryButton}>
          {mutation.isPending ? 'Filing…' : `File as ${KIND_LABELS[draft.kind]}`}
        </button>
        <button type="button" onClick={onCancel} disabled={mutation.isPending} className={outlineButton}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-on-surface-variant">{label}</span>
      {children}
    </label>
  );
}
